import { useEffect, useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import QRCode from 'qrcode';
import { useToast } from '../../components/ui';

// The two links people are invited to: the website (opens in any browser) and
// the Play Store listing of the APK. QR images are drawn locally (no network),
// so they work in the APK offline too.
export const SHARE_LINKS = [
  {
    key: 'web',
    title: 'Open in browser',
    hint: 'Scan with any phone camera to open Mehfil in the browser — nothing to install.',
    url: 'https://mehfil.smartifytech.in/#/app/dashboard',
    file: 'mehfil-web-qr.png',
  },
  {
    key: 'play',
    title: 'Get the Android app',
    hint: 'Scan to open Mehfil by Smartify on the Google Play Store.',
    url: 'https://play.google.com/store/apps/details?id=com.smartify.mehfil',
    file: 'mehfil-playstore-qr.png',
  },
];

const openExternal = (url) => window.open(url, Capacitor.isNativePlatform() ? '_system' : '_blank', 'noopener');

export function useShareApp() {
  const toast = useToast();
  const [qr, setQr] = useState({}); // key → PNG data URL

  useEffect(() => {
    let alive = true;
    Promise.all(SHARE_LINKS.map(l =>
      QRCode.toDataURL(l.url, { width: 512, margin: 2, errorCorrectionLevel: 'M' }).then(src => [l.key, src])
    )).then(pairs => { if (alive) setQr(Object.fromEntries(pairs)); })
      .catch(() => toast && toast('Could not draw the QR codes'));
    return () => { alive = false; };
  }, [toast]);

  const copy = useCallback(async (link) => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast('Link copied');
    } catch {
      window.prompt('Copy this link', link.url);
    }
  }, [toast]);

  const open = useCallback((link) => openExternal(link.url), []);

  const whatsapp = useCallback((link) => {
    openExternal(`https://wa.me/?text=${encodeURIComponent(`${link.title} — Mehfil by Smartify\n${link.url}`)}`);
  }, []);

  // OS share sheet: the QR image + link when files can be shared, else the link only.
  const share = useCallback(async (link) => {
    const text = `${link.title} — Mehfil by Smartify`;
    try {
      if (navigator.share) {
        const src = qr[link.key];
        if (src && navigator.canShare) {
          const blob = await (await fetch(src)).blob();
          const file = new File([blob], link.file, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: text, text: `${text}\n${link.url}` });
            return;
          }
        }
        await navigator.share({ title: text, text, url: link.url });
        return;
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
    }
    whatsapp(link);
  }, [qr, whatsapp]);

  const download = useCallback((link) => {
    const src = qr[link.key];
    if (!src) return;
    const a = document.createElement('a');
    a.href = src;
    a.download = link.file;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [qr]);

  return {
    links: SHARE_LINKS.map(l => ({ ...l, qr: qr[l.key] })),
    canShare: typeof navigator !== 'undefined' && !!navigator.share,
    copy, open, share, whatsapp, download,
  };
}
